import { memo, useState } from "react";
import type { SyntheticEvent } from "react";
import Button from "../../../../../../../../../../components/Button/Button";
import ExternalLinkIcon from "../../../../../../../../../../assets/svg/external-link-icon";
import {
  getClampedHtmlPreviewHeight,
  openUrlInNewWindow,
} from "../../../utils/fileManagement.utils";
import { HTML_PREVIEW_DEFAULT_HEIGHT_PX } from "../../../constants/fileManagement.constants";
import type { HtmlFilePreviewProps } from "../../../types/fileManagement.types";

// Renders an HTML file inline inside a sandboxed iframe. The iframe is
// interactive so the embedded page can be scrolled; a floating button in the
// top-right corner opens the file in a new tab. On load we try to size the
// wrapper to the actual content height (clamped) so short pages don't leave
// a large blank area at the bottom. For cross-origin URLs the measurement
// is blocked by the browser, so we keep the default height.
const HtmlFilePreview = ({ fileUrl, fileName }: HtmlFilePreviewProps) => {
  const [previewHeightPx, setPreviewHeightPx] = useState<number>(
    HTML_PREVIEW_DEFAULT_HEIGHT_PX
  );

  const handleOpenInNewWindow = () => openUrlInNewWindow(fileUrl);

  const handleIframeLoad = (event: SyntheticEvent<HTMLIFrameElement>) => {
    const clampedHeight = getClampedHtmlPreviewHeight(event.currentTarget);
    if (clampedHeight !== null) setPreviewHeightPx(clampedHeight);
  };

  return (
    <div
      className="group w-full rounded-lg overflow-hidden border border-border-gray dark:border-border-gray-dk bg-fixed-white-light-and-dark-mode dark:bg-fixed-white-light-and-dark-mode-dk relative transition-[height] duration-200"
      style={{ height: `${previewHeightPx}px` }}
    >
      <iframe
        src={fileUrl}
        title={fileName}
        sandbox="allow-same-origin"
        loading="lazy"
        onLoad={handleIframeLoad}
        className="w-full h-full border-0"
      />
      <Button
        Icon={ExternalLinkIcon}
        iconLeft
        iconClass="w-4 h-4 text-verba-blue dark:text-verba-blue-dk"
        onClick={handleOpenInNewWindow}
        ariaLabel={`Open ${fileName} in new tab`}
        buttonContainerClass="absolute top-2 right-2 w-7 h-7 rounded-md bg-fixed-white-light-and-dark-mode/90 dark:bg-fixed-white-light-and-dark-mode-dk/90 border border-border-gray dark:border-border-gray-dk shadow-sm opacity-0 group-hover:opacity-100 transition-opacity hover:border-verba-blue dark:hover:border-verba-blue-dk cursor-pointer"
      />
    </div>
  );
};

const arePropsEqual = (
  prev: HtmlFilePreviewProps,
  next: HtmlFilePreviewProps
) => prev.fileUrl === next.fileUrl && prev.fileName === next.fileName;

export default memo(HtmlFilePreview, arePropsEqual);
